import { useCallback } from 'react';
import { useAuthRefreshMutation } from '@/entities/auth/api';
import { TokenPairDto } from '@/app/store/api/restApi';
import { getRefreshToken, setTokens } from './tokenStorage';

const REFRESH_BUFFER_SECONDS = 60;

let timerHandle: ReturnType<typeof setTimeout> | null = null;

export const cancelRefreshTimer = (): void => {
  if (timerHandle !== null) {
    clearTimeout(timerHandle);
    timerHandle = null;
  }
};

const setTimer = (expiresInSeconds: number, onFire: () => void): void => {
  cancelRefreshTimer();
  const delayMs = Math.max(0, (expiresInSeconds - REFRESH_BUFFER_SECONDS) * 1000);
  timerHandle = setTimeout(onFire, delayMs);
};

export const useTokenRefresh = () => {
  const [refreshTokens] = useAuthRefreshMutation();

  const refresh = useCallback(async (): Promise<TokenPairDto> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const result = await refreshTokens({ refreshTokenDto: { refreshToken } }).unwrap();
    setTokens(result.data);
    setTimer(result.data.expiresInSeconds, () => {
      void refresh().catch(() => undefined);
    });
    return result.data;
  }, [refreshTokens]);

  const scheduleRefreshTimer = useCallback(
    (expiresInSeconds: number) => {
      setTimer(expiresInSeconds, () => {
        void refresh().catch(() => undefined);
      });
    },
    [refresh],
  );

  return { refresh, scheduleRefreshTimer };
};
